import React, { useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { PasswordStrengthIndicator, PasswordStrengthTexts } from './PasswordStrengthIndicator';
import { getPasswordStrength } from '../../services/passwordStrengthIndicator';
import { ReactComponent as EyeOpened } from '../../assets/icons/svg/shared/ic-shared-eye-opened.svg';
import { ReactComponent as EyeClosed } from '../../assets/icons/svg/shared/ic-shared-eye-closed.svg';
import { ReactComponent as InfoIcon } from '../../assets/svg/infoIconInitial.svg';
import { LiliTooltip, LiliToolTipColor, TooltipPosition } from '../LiliTooltip/LiliTooltip';
import { LiliFloatingLabel } from '../LiliFloatingLabel/LiliFloatingLabel';
import { DEFAULT_INPUT_STYLE, FILLED_INPUT_CLASS } from '../LiliFloatingLabel/consts';
import { LiliInputBaseProps } from '../LiliMaskedInput/LiliMaskedInput';

export interface LiliPasswordInputProps extends LiliInputBaseProps {
  onChange: (input: string) => void;
  styles?: string;
  passwordStrengthHints?: string[];
  passwordStrengthTexts?: PasswordStrengthTexts;
  inilineChild?: React.ReactNode;
}

const PasswordTooltip = ({ hints }: { hints: string[] }) => (
  <div className='flex flex-col py-[12px] px-[16px]'>
    {hints.map((text) => (
      <div className='flex flex-row gap-2'>
        <p className='text-[14px] text-bw07 font-metricR'>•</p>
        <p className='text-[14px] text-bw07 font-metricR text-left'>{text}</p>
      </div>
    ))}
  </div>
);
export const LiliPasswordInput = ({
  id,
  onChange,
  errorMessage,
  inilineChild,
  styles,
  title,
  passwordStrengthHints,
  passwordStrengthTexts,
}: LiliPasswordInputProps) => {
  const [passwordInput, setPasswordInput] = useState<string>('');
  const [passwordStrength, setPasswordStrength] = useState<number>(0);
  const [inputType, setInputType] = useState<'text' | 'password'>('password');

  const onInputChange = (value: string) => {
    const strength = getPasswordStrength(value).id;
    setPasswordInput(value);
    setPasswordStrength(strength);
    onChange(value);
  };
  return (
    <form onSubmit={(event) => event.preventDefault()} className={`relative ${styles || ''}`}>
      <LiliFloatingLabel errorMessage={errorMessage} label={title} inilineChild={inilineChild}>
        <div className='flex flex-row items-center mt-[6px]'>
          {/* Full API: https://primereact.org/inputtext/ */}
          <InputText
            type={inputType}
            className={`${passwordInput ? FILLED_INPUT_CLASS : ''} ${DEFAULT_INPUT_STYLE} mt-[6px]`}
            unstyled
            placeholder={' '}
            id={id}
            onChange={(event) => onInputChange(event.target.value)}
            value={passwordInput}
          />
          <button
            id={`showHidePassword-${id}`}
            className='mb-[6px]'
            type='button'
            onClick={() => setInputType(inputType === 'password' ? 'text' : 'password')}
          >
            {inputType === 'password' ? <EyeOpened className='text-bw07' /> : <EyeClosed className='text-bw07' />}
          </button>
          {passwordStrengthHints && passwordStrengthHints.length > 0 && (
            <div>
              <InfoIcon
                data-tooltip-id='password-hint'
                data-tooltip-position-strategy='fixed'
                className='ml-[4px] w-[20px] h-[20px] mb-[6px]'
              />
              <LiliTooltip
                description=''
                targetId='password-hint'
                position={TooltipPosition.RIGHT}
                color={LiliToolTipColor.White}
                customContent={<PasswordTooltip hints={passwordStrengthHints} />}
              />
            </div>
          )}
        </div>
      </LiliFloatingLabel>
      {passwordInput.length > 0 && !errorMessage && passwordStrengthTexts && (
        <div className='h-[12px] mt-[-18px]'>
          <PasswordStrengthIndicator strength={passwordStrength} passwordStrengthTexts={passwordStrengthTexts} />
        </div>
      )}
    </form>
  );
};
